/**
 * Design System - Select Component
 *
 * A flexible select dropdown component with label, error, and hint support.
 *
 * @example
 * <Select
 *   label="Role"
 *   options={[
 *     { value: 'admin', label: 'Administrator' },
 *     { value: 'user', label: 'User' }
 *   ]}
 *   required
 * />
 */

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';

const Select = forwardRef(({
  label,
  error,
  hint,
  options = [],
  placeholder = 'Select an option',
  required = false,
  disabled = false,
  fullWidth = true,
  className,
  id,
  ...props
}, ref) => {
  // Generate unique ID if not provided
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${selectId}-error`;
  const hintId = `${selectId}-hint`;

  // Base select styles
  const baseStyles = 'px-3 py-2 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 text-gray-900 bg-white';

  // State styles
  const stateStyles = error
    ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
    : 'border-gray-300 focus:border-primary-500';

  // Disabled styles
  const disabledStyles = disabled
    ? 'bg-gray-100 cursor-not-allowed opacity-60'
    : 'bg-white cursor-pointer';

  // Width styles
  const widthStyle = fullWidth ? 'w-full' : '';

  return (
    <div className={cn('form-field', className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && (
            <span className="text-danger-500 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {/* Select */}
      <select
        ref={ref}
        id={selectId}
        disabled={disabled}
        aria-invalid={!!error}
        aria-required={required}
        aria-describedby={cn(
          error && errorId,
          hint && hintId
        )}
        className={cn(
          baseStyles,
          stateStyles,
          disabledStyles,
          widthStyle
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>

      {/* Error message */}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-sm text-danger-600"
        >
          {error}
        </p>
      )}

      {/* Hint text */}
      {hint && !error && (
        <p
          id={hintId}
          className="mt-1 text-sm text-gray-600"
        >
          {hint}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

Select.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  hint: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
};

export default Select;
